import { memo, useCallback, useState, useEffect } from 'react';
import { Card, ProgressBar, Badge, Button } from 'react-bootstrap';

/**
 * Virtualized Section Progress List Component
 * Renders only visible sections using a paging approach
 * Optimized for large courses (40+ sections, 400+ questions)
 * 
 * Performance improvements:
 * - Renders 10 sections at a time (virtualization)
 * - Lazy loads section details on demand
 * - Uses React.memo to prevent unnecessary re-renders
 * - Pagination for smooth scrolling
 */
const SectionItem = memo(({ section, progress, index, onLoadSection }) => {
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Lazy load section details when visible
    if (!loaded) {
      onLoadSection?.(section.id);
      setLoaded(true);
    }
  }, [section.id, loaded, onLoadSection]);

  const isUnlocked = progress.isSectionUnlocked(section.id);
  const isCompleted = progress.isSectionCompleted(section.id);
  const sectionProgress = progress.getSectionProgress(section.id);
  const score = sectionProgress?.score || 0;

  // Calculate questionsAttempted from per-question progress (handles missing DB field)
  const questionsAttempted = (section.questions || []).filter(q => {
    const qp = progress.getQuestionProgress(q.id);
    return (qp?.timesShown ?? qp?.attempts ?? 0) > 0;
  }).length;

  return (
    <div 
      className={`section-progress-item ${!isUnlocked ? 'locked' : ''}`}
      style={{ minHeight: '80px', padding: '12px', borderBottom: '1px solid #eee' }}
    >
      <div className="section-info">
        <div className="d-flex align-items-center mb-2">
          <span className="section-number me-2" style={{ minWidth: '24px', fontWeight: 'bold' }}>
            {index + 1}
          </span>
          <h6 className="mb-0 flex-grow-1" style={{ fontSize: '0.95rem' }}>
            {section.title}
          </h6>
          {!isUnlocked && <Badge bg="secondary">Locked</Badge>}
          {/* F5 (PRD): Score-based status labels — Mastered (95%+), Learned (85%+), In Progress */}
          {isUnlocked && score >= 95 && (
            <Badge bg="warning" text="dark">🏆 Mastered</Badge>
          )}
          {isUnlocked && score >= 85 && score < 95 && (
            <Badge bg="success">✨ Learned</Badge>
          )}
          {isUnlocked && score > 0 && score < 85 && (
            <Badge bg="primary">In Progress</Badge>
          )}
        </div>
        
        {isUnlocked && (
          <>
            <ProgressBar 
              now={score} 
              variant={getScoreVariant(score)}
              className="mb-2"
              style={{ height: '6px' }}
            />
            <div className="section-stats">
              <small className="text-muted">
                Score: {Math.round(score)}% | 
                Questions: {questionsAttempted}/{section.questions?.length || 0}
              </small>
              {(sectionProgress?.decayedAmount || 0) > 0.5 && (
                <div>
                  <small className="text-warning">
                    📉 Lost {Math.round(sectionProgress.decayedAmount)}% since last session
                  </small>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom memo comparison: only re-render if critical props change
  const prevSP = prevProps.progress.getSectionProgress(prevProps.section.id);
  const nextSP = nextProps.progress.getSectionProgress(nextProps.section.id);
  const prevSectionScore = prevSP?.score || 0;
  const nextSectionScore = nextSP?.score || 0;
  const prevDecayedAmount = prevSP?.decayedAmount || 0;
  const nextDecayedAmount = nextSP?.decayedAmount || 0;

  // Also check if any question progress in this section changed
  const prevAttempted = (prevProps.section.questions || []).filter(q =>
    (prevProps.progress.getQuestionProgress(q.id)?.timesShown ?? 0) > 0
  ).length;
  const nextAttempted = (nextProps.section.questions || []).filter(q =>
    (nextProps.progress.getQuestionProgress(q.id)?.timesShown ?? 0) > 0
  ).length;

  return prevProps.section.id === nextProps.section.id &&
         prevSectionScore === nextSectionScore &&
         prevDecayedAmount === nextDecayedAmount &&
         prevAttempted === nextAttempted &&
         prevProps.index === nextProps.index;
});

SectionItem.displayName = 'SectionItem';

/**
 * Main virtualized list component
 */
export function VirtualizedSectionProgressList({ course, progress, onLoadSection }) {
  const [page, setPage] = useState(0);
  const itemsPerPage = 10; // Render 10 sections at a time

  const sections = course?.sections || [];
  const totalPages = Math.ceil(sections.length / itemsPerPage);
  const visibleSections = sections.slice(
    page * itemsPerPage,
    (page + 1) * itemsPerPage
  );

  const handleNextPage = useCallback(() => {
    if (page < totalPages - 1) {
      setPage(p => p + 1);
    }
  }, [page, totalPages]);

  const handlePrevPage = useCallback(() => {
    if (page > 0) {
      setPage(p => p - 1);
    }
  }, [page]);

  if (!course || !progress) {
    return null;
  }

  return (
    <Card className="section-progress-list">
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Section Progress</h5>
          <small className="text-muted">
            {sections.length > 0 ? `${page * itemsPerPage + 1}-${Math.min((page + 1) * itemsPerPage, sections.length)} of ${sections.length}` : '—'}
          </small>
        </div>
      </Card.Header>
      <Card.Body className="p-0">
        {sections.length === 0 ? (
          <div className="text-center py-4 text-muted">
            No sections available
          </div>
        ) : (
          <>
            {/* Virtualized Section Items */}
            {visibleSections.map((section, idx) => (
              <SectionItem
                key={section.id}
                section={section}
                progress={progress}
                index={page * itemsPerPage + idx}
                onLoadSection={onLoadSection}
              />
            ))}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3" style={{ borderTop: '1px solid #eee' }}>
                <Button 
                  size="sm" 
                  variant="outline-secondary"
                  disabled={page === 0}
                  onClick={handlePrevPage}
                >
                  ← Previous
                </Button>
                <small className="text-muted">
                  Page {page + 1} of {totalPages}
                </small>
                <Button 
                  size="sm" 
                  variant="outline-secondary"
                  disabled={page === totalPages - 1}
                  onClick={handleNextPage}
                >
                  Next →
                </Button>
              </div>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  );
}

function getScoreVariant(score) {
  if (score >= 85) return 'success';
  if (score >= 70) return 'info';
  if (score >= 60) return 'warning';
  return 'danger';
}
